// src/features/tuition/components/online/MicroActivityForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Grid,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../../styles/theme';

const MicroActivityForm = ({ open, onClose, onSave, initialData = null }) => {
  const [formData, setFormData] = useState({
    title: '',
    type: 'quiz',
    description: '',
    timeToComplete: 5,
    isRequired: true,
    isTimestamp: true,
    options: [],
    correctAnswer: '',
    points: 10
  });
  
  const [newOption, setNewOption] = useState('');
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        type: initialData.type || 'quiz',
        description: initialData.description || '',
        timeToComplete: initialData.timeToComplete || 5,
        isRequired: initialData.isRequired !== undefined ? initialData.isRequired : true,
        isTimestamp: initialData.isTimestamp !== undefined ? initialData.isTimestamp : true,
        options: initialData.options || [],
        correctAnswer: initialData.correctAnswer || '',
        points: initialData.points || 10
      });
    } else {
      resetForm();
    }
  }, [initialData, open]);
  
  const resetForm = () => {
    setFormData({
      title: '',
      type: 'quiz',
      description: '',
      timeToComplete: 5,
      isRequired: true,
      isTimestamp: true,
      options: [],
      correctAnswer: '',
      points: 10
    });
    setNewOption('');
    setErrors({});
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  
  const handleAddOption = () => {
    if (!newOption.trim()) return;
    
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, newOption]
    }));
    setNewOption('');
  };
  
  const handleDeleteOption = (index) => {
    const updatedOptions = [...formData.options];
    updatedOptions.splice(index, 1);
    
    // Update correct answer if deleted option was the correct one
    let updatedCorrectAnswer = formData.correctAnswer;
    if (formData.correctAnswer === formData.options[index]) {
      updatedCorrectAnswer = '';
    }
    
    setFormData(prev => ({
      ...prev,
      options: updatedOptions,
      correctAnswer: updatedCorrectAnswer
    }));
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (formData.type === 'quiz' && formData.options.length < 2) {
      newErrors.options = 'At least 2 options are required for a quiz';
    }
    
    if (formData.type === 'quiz' && !formData.correctAnswer) {
      newErrors.correctAnswer = 'Please select a correct answer';
    }
    
    setErrors(newErrors);
    
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = () => {
    if (!validateForm()) return;
    
    const activityData = {
      ...formData,
      id: initialData ? initialData.id : Date.now().toString(),
      createdAt: initialData ? initialData.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    onSave(activityData);
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="bold">
            {initialData ? 'Edit Activity' : 'Add New Activity'}
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Activity Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              error={!!errors.title}
              helperText={errors.title}
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Activity Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                label="Activity Type"
                onChange={handleInputChange}
              >
                <MenuItem value="quiz">Multiple Choice Quiz</MenuItem>
                <MenuItem value="reflection">Reflection Question</MenuItem>
                <MenuItem value="checkpoint">Checkpoint</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
              <TimeIcon sx={{ color: 'text.secondary', mr: 1 }} />
              <TextField
                fullWidth
                label="Time to Complete (minutes)"
                name="timeToComplete"
                type="number"
                value={formData.timeToComplete}
                onChange={handleInputChange}
                inputProps={{ min: 1 }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Points"
              name="points"
              type="number"
              value={formData.points}
              onChange={handleInputChange}
              inputProps={{ min: 0 }}
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch 
                  checked={formData.isRequired} 
                  onChange={handleSwitchChange} 
                  name="isRequired" 
                  color="primary" 
                />
              }
              label="Required Activity"
              sx={{ mb: 2 }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch 
                  checked={formData.isTimestamp} 
                  onChange={handleSwitchChange} 
                  name="isTimestamp" 
                  color="primary" 
                />
              }
              label="Track Completion Time"
              sx={{ mb: 2 }}
            />
          </Grid>
          
          {formData.type === 'quiz' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Quiz Options
              </Typography>
              
              <Box display="flex" sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Add Option"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddOption}
                  disabled={!newOption.trim()}
                  sx={{
                    bgcolor: THEME_COLORS.primary,
                    '&:hover': { bgcolor: '#1e1c4e' }
                  }}
                >
                  Add
                </Button>
              </Box>
              
              {errors.options && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {errors.options}
                </Typography>
              )}
              
              <Box sx={{ mb: 3 }}>
                {formData.options.length > 0 ? (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Options:
                    </Typography>
                    {formData.options.map((option, index) => (
                      <Box 
                        key={index} 
                        display="flex" 
                        alignItems="center" 
                        sx={{ 
                          p: 1,
                          mb: 1,
                          border: '1px solid',
                          borderColor: formData.correctAnswer === option ? '#4CAF50' : '#e0e0e0',
                          borderRadius: 1,
                          bgcolor: formData.correctAnswer === option ? 'rgba(76, 175, 80, 0.08)' : 'transparent'
                        }}
                      >
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {option}
                        </Typography>
                        <Box>
                          <Button
                            size="small"
                            onClick={() => setFormData(prev => ({ ...prev, correctAnswer: option }))}
                            color={formData.correctAnswer === option ? "success" : "inherit"}
                            sx={{ mr: 1 }}
                          >
                            {formData.correctAnswer === option ? 'Correct' : 'Set as Correct'}
                          </Button>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteOption(index)}
                            color="error"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No options added yet. Add at least two options for your quiz.
                  </Typography>
                )}
              </Box>
              
              {errors.correctAnswer && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {errors.correctAnswer}
                </Typography>
              )}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{
            bgcolor: THEME_COLORS.primary,
            '&:hover': { bgcolor: '#1e1c4e' }
          }}
        >
          {initialData ? 'Update' : 'Add'} Activity
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MicroActivityForm;